import { fadeIn, updateColors } from "../../../scripts/animations";
import {
  toggleDisplay,
  initSection,
  updateDynamicElems,
} from "../../../scripts/utils";
import {
  Slider,
  Accordion,
  ComboBox,
  QtySelector,
  AddToCart,
} from "../../../scripts/global";
import { component } from "picoapp";

/**
 * Contains the implementation of the subscription form.
 * It initializes and updates various elements and components on the page based on user interaction
 * by searching for elements with specific data attributes ( [data-update] ) and replacing their content with the new value.
 *
 * @module camilaForm
 *
 * @property {Element} container - The subscription barba container element
 * @property {Element} form - The product form element
 * @property {QtySelector} qty_selector - The quantity selector component instance
 * @property {ComboBox} combo_box - The combo box component instance
 * @property {AddToCart} atc_button - The add to cart button component instance
 * @property {NodeList} all_swatches - A list of all swatch elements in the form
 * @property {NodeList} dynamic_elems - A list of all dynamic elements to update when a swatch is clicked
 * @property {Element} accordion_group_container - The container element for all accordion groups
 * @property {Element} reviews_container - The container element for product reviews
 */
interface State {
  container: Element | null;
  form: Element | null;
  priceDisplay: Element | null;
  qty_selector: QtySelector | null;
  combo_box: ComboBox | null;
  atc_button: AddToCart | null;
  all_swatches: NodeList | null;
  dynamic_elems: NodeList | null;
  accordion_group_container: Element | null;
  reviews_container: Element | null;
}

const state: State = {
  container: null,
  form: null,
  priceDisplay: null,
  qty_selector: null,
  combo_box: null,
  atc_button: null,
  all_swatches: null,
  dynamic_elems: null,
  accordion_group_container: null,
  reviews_container: null,
};

/**
 * Initializes the subscription form by setting up various elements and components.
 */
const _initForm = () => {
  state.container = document.getElementById("subscription-barba-container");
  state.form = document.querySelector("product-form");
  state.priceDisplay = document.getElementById("price-display-box");

  if (!state.container || !state.form) return;

  try {
    state.qty_selector = new QtySelector(state.form);
    state.combo_box = new ComboBox(state.form, state.priceDisplay);
    state.atc_button = new AddToCart(
      state.form,
      state.qty_selector,
      state.combo_box
    );
  } catch (e) {
    console.error(e);
  }

  state.all_swatches = state.form.querySelectorAll(".product-swatch");
  state.dynamic_elems = document.querySelectorAll("[data-update]");
  state.accordion_group_container = document.getElementById(
    "accordion-group-container"
  );
  state.reviews_container = document.getElementById("reviews_container");
  state.all_swatches.forEach((swatchEl: HTMLElement) => {
    swatchEl.addEventListener("click", updateProductElements);
  });

  const initialSwatch = state.all_swatches[0] as HTMLElement;
  updateColors(initialSwatch);

  fadeIn(state.container as HTMLElement);
};

/**
 * Event handler that updates various elements on the page based on the clicked swatch.
 * @param {Event} e - The event object triggered by the click event.
 */
const updateProductElements = (e: Event) => {
  const newSwatchEl = e.currentTarget as HTMLElement;
  const newProductHandle = newSwatchEl.dataset.handle;

  updateColors(newSwatchEl);
  toggleDisplay(
    newProductHandle,
    state.accordion_group_container as Element,
    "product-accordion"
  );
  updateDynamicElems(newSwatchEl, state.dynamic_elems, state.form);
};

export const customProductForm = component(() => {
  _initForm();
  initSection("slider-container", Slider);
  initSection("accordion-group", Accordion);
});
